import React, { useState, useMemo } from "react";
import moment from "moment-timezone";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import type { ImportPreviewEntry } from "api/MulchMap";

/**
 * ImportMapDedupModal — Session 24
 *
 * Shown after `previewImportMulchMap` returns at least one entry whose
 * `duplicateOf` is non-null. Each such entry gets its own row where the
 * rep picks New / Overwrite / Skip. Non-duplicate entries default to
 * 'new' (backend behavior when a key is missing from `entryChoices`) and
 * are surfaced only as a summary line at the top.
 *
 * The parent hands us the raw preview `entries` list. Confirm returns
 * the per-index choice map; Cancel closes without side effects.
 */

export type EntryChoice = "new" | "overwrite" | "skip";

interface Props {
  siteLabel: string;
  entries: ImportPreviewEntry[];
  onCancel: () => void;
  onConfirm: (
    choices: Record<
      number,
      { choice: EntryChoice; targetMapGroupId?: string }
    >,
  ) => void;
}

const styles: Record<string, React.CSSProperties> = {
  intro: {
    fontSize: 13,
    color: "#5D5E62",
    marginTop: 0,
    marginBottom: 16,
    lineHeight: 1.5,
  },
  cleanSummary: {
    fontSize: 12,
    color: "#5D5E62",
    marginBottom: 12,
    padding: "8px 12px",
    background: "#F3F4F6",
    borderRadius: 6,
  },
  entryList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  entryCard: {
    border: "1px solid #E4E7EB",
    borderRadius: 8,
    padding: 12,
    background: "#FFFFFF",
  },
  entryHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 8,
  },
  entryTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#111",
    margin: 0,
  },
  entrySub: {
    fontSize: 12,
    color: "#5D5E62",
    marginTop: 2,
  },
  vs: {
    fontSize: 11,
    color: "#B0B4BA",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 600,
    marginTop: 8,
    marginBottom: 4,
  },
  existingBlock: {
    fontSize: 12,
    color: "#374151",
    background: "#FFF7ED",
    border: "1px solid #FED7AA",
    borderRadius: 6,
    padding: "8px 10px",
    marginBottom: 10,
  },
  radioRow: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginTop: 6,
  },
  radioOption: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    fontSize: 13,
    color: "#111",
    cursor: "pointer",
    padding: "4px 0",
  },
  radioHint: {
    fontSize: 11,
    color: "#5D5E62",
    marginLeft: 24,
    marginTop: 2,
  },
  bulkRow: {
    flex: 1,
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "flex-start",
    fontSize: 12,
    color: "#5D5E62",
  },
  bulkButton: {
    background: "transparent",
    border: "1px solid #E4E7EB",
    borderRadius: 4,
    padding: "3px 8px",
    fontSize: 11,
    cursor: "pointer",
    color: "#111",
  },
};

const ImportMapDedupModal: React.FC<Props> = ({
  siteLabel,
  entries,
  onCancel,
  onConfirm,
}) => {
  const { duplicates, cleanCount } = useMemo(() => {
    const d = entries.filter((e) => e.duplicateOf !== null);
    return { duplicates: d, cleanCount: entries.length - d.length };
  }, [entries]);

  // Default every duplicate to 'skip' — the safest default. Reps have to
  // deliberately pick New or Overwrite. Skip means no side effect if they
  // hit Confirm without touching anything.
  const [choices, setChoices] = useState<Record<number, EntryChoice>>(() => {
    const init: Record<number, EntryChoice> = {};
    for (const e of duplicates) init[e.index] = "skip";
    return init;
  });

  const setChoice = (index: number, choice: EntryChoice) => {
    setChoices((prev) => ({ ...prev, [index]: choice }));
  };

  const setAll = (choice: EntryChoice) => {
    const next: Record<number, EntryChoice> = {};
    for (const e of duplicates) next[e.index] = choice;
    setChoices(next);
  };

  const handleConfirm = () => {
    const result: Record<
      number,
      { choice: EntryChoice; targetMapGroupId?: string }
    > = {};
    for (const e of duplicates) {
      const c = choices[e.index] ?? "skip";
      result[e.index] = {
        choice: c,
        targetMapGroupId:
          c === "overwrite" && e.duplicateOf
            ? e.duplicateOf.mapGroupId
            : undefined,
      };
    }
    // Clean (non-duplicate) entries deliberately not added — backend
    // defaults missing keys to 'new', which is exactly what we want.
    onConfirm(result);
  };

  return (
    <Modal isOpen={true} toggle={onCancel} size="lg" centered>
      <ModalHeader toggle={onCancel}>
        {duplicates.length} map{duplicates.length === 1 ? "" : "s"} already
        exist{duplicates.length === 1 ? "s" : ""} at this site
      </ModalHeader>
      <ModalBody>
        <p style={styles.intro}>
          {duplicates.length === 1 ? (
            <>
              This import contains a map that matches an existing one at{" "}
              <strong>{siteLabel || "this site"}</strong> by name and client.
              Pick what to do with it before importing.
            </>
          ) : (
            <>
              {duplicates.length} of the {entries.length} maps in this file
              match existing maps at{" "}
              <strong>{siteLabel || "this site"}</strong> by name and client.
              Pick what to do with each one before importing.
            </>
          )}
        </p>

        {cleanCount > 0 && (
          <div style={styles.cleanSummary}>
            {cleanCount} other map{cleanCount === 1 ? "" : "s"} in the file{" "}
            {cleanCount === 1 ? "doesn't" : "don't"} match anything at this
            site — {cleanCount === 1 ? "it" : "they"} will import as new.
          </div>
        )}

        <div style={styles.entryList}>
          {duplicates.map((entry) => {
            const chosen = choices[entry.index] ?? "skip";
            const dup = entry.duplicateOf;
            // Defensive early-return — duplicates was already filtered
            // to non-null, but ESLint's flow analysis doesn't carry that
            // across the useMemo boundary.
            if (!dup) return null;
            return (
              <div key={entry.index} style={styles.entryCard}>
                <div style={styles.entryHead}>
                  <div>
                    <div style={styles.entryTitle}>{entry.name}</div>
                    <div style={styles.entrySub}>
                      From file{" "}
                      {entry.clientName ? (
                        <>
                          · client <strong>{entry.clientName}</strong>
                          {!entry.clientResolved && (
                            <span style={{ color: "#B45309", marginLeft: 6 }}>
                              (not matched in your account)
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ color: "#B0B4BA" }}>· no client</span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={styles.vs}>Existing at this site</div>
                <div style={styles.existingBlock}>
                  <strong>{dup.name || "Untitled map"}</strong> · v{dup.version}
                  {dup.clientName && (
                    <>
                      {" "}
                      · client <strong>{dup.clientName}</strong>
                    </>
                  )}
                  <div style={{ fontSize: 11, marginTop: 2, color: "#78716C" }}>
                    Edited {moment(dup.updatedAt).fromNow()}
                  </div>
                </div>

                <div style={styles.radioRow}>
                  <label style={styles.radioOption}>
                    <input
                      type="radio"
                      name={`choice-${entry.index}`}
                      checked={chosen === "skip"}
                      onChange={() => setChoice(entry.index, "skip")}
                    />
                    <div>
                      Skip this map
                      <div style={styles.radioHint}>
                        Don&apos;t import this entry. Existing map stays as-is.
                      </div>
                    </div>
                  </label>
                  <label style={styles.radioOption}>
                    <input
                      type="radio"
                      name={`choice-${entry.index}`}
                      checked={chosen === "overwrite"}
                      onChange={() => setChoice(entry.index, "overwrite")}
                    />
                    <div>
                      Overwrite the existing map (adds a new version)
                      <div style={styles.radioHint}>
                        Appends the imported map as v{dup.version + 1} of{" "}
                        <strong>{dup.name || "Untitled map"}</strong>. Version
                        history is preserved.
                      </div>
                    </div>
                  </label>
                  <label style={styles.radioOption}>
                    <input
                      type="radio"
                      name={`choice-${entry.index}`}
                      checked={chosen === "new"}
                      onChange={() => setChoice(entry.index, "new")}
                    />
                    <div>
                      Import as a new map
                      <div style={styles.radioHint}>
                        Creates a second map with the same name at this site.
                        Only pick this if the duplicate detection is wrong.
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </ModalBody>
      <ModalFooter>
        {duplicates.length > 1 && (
          <div style={styles.bulkRow}>
            <span>Apply to all:</span>
            <button
              type="button"
              onClick={() => setAll("overwrite")}
              style={styles.bulkButton}
            >
              Overwrite all
            </button>
            <button
              type="button"
              onClick={() => setAll("skip")}
              style={styles.bulkButton}
            >
              Skip all
            </button>
          </div>
        )}
        <BasicButton
          type="button"
          scheme="secondary"
          outline
          onClick={onCancel}
          styles={{ margin: "0" }}
          keepText
        >
          Cancel import
        </BasicButton>
        <BasicButton
          type="button"
          onClick={handleConfirm}
          styles={{ margin: "0" }}
          keepText
        >
          Import with these choices
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default ImportMapDedupModal;
